import React, { useState } from "react";
import { ITreatment, ISubTreatment } from "../types/clinic.types";
import { ConfirmationModal } from "./ConfirmationModal";
import { TreatmentCard } from "./TreatmentCard";
import { TreatmentFormModal } from "./TreatmentFormModal";
import { SubTreatmentFormModal } from "./SubTreatmentFormModal";
import AddIcon from "@mui/icons-material/Add";
import baseUrl from "../../../../api/baseUrl";
import { toast } from "react-toastify";

interface Props {
  treatments: ITreatment[];
  onTreatmentsChange: (treatments: ITreatment[]) => void;
}

export const Treatments: React.FC<Props> = ({
  treatments,
  onTreatmentsChange,
}) => {
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);
  const [isDeletingTreatment, setIsDeletingTreatment] = useState(false);

  const [isTreatmentModalOpen, setIsTreatmentModalOpen] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState<ITreatment | null>(
    null
  );

  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<ISubTreatment | null>(null);
  const [parentTreatmentId, setParentTreatmentId] = useState<string | null>(
    null
  );

  const closeTreatmentModal = () => {
    setIsTreatmentModalOpen(false);
    setEditingTreatment(null);
  };

  const closeSubModal = () => {
    setIsSubModalOpen(false);
    setEditingSub(null);
    setParentTreatmentId(null);
  };

  const handleOpenAddTreatment = () => {
    setEditingTreatment(null);
    setIsTreatmentModalOpen(true);
  };

  const handleOpenEditTreatment = (treatment: ITreatment) => {
    setEditingTreatment(treatment);
    setIsTreatmentModalOpen(true);
  };

  const handleSaveTreatment = (treatmentData: ITreatment) => {
    if (editingTreatment) {
      onTreatmentsChange(
        treatments.map((t) =>
          t.id === editingTreatment.id
            ? { ...t, ...treatmentData, subTreatments: t.subTreatments }
            : t
        )
      );
    } else {
      onTreatmentsChange([
        ...treatments,
        { ...treatmentData, subTreatments: [] },
      ]);
    }
    closeTreatmentModal();
  };

  const handleDeleteTreatment = () => {
    if (!deleteCandidate || isDeletingTreatment) return;

    const previousTreatments = treatments;
    setIsDeletingTreatment(true);

    baseUrl
      .patch("/client/booking/deleteBookTreatments", {
        treatmentId: deleteCandidate,
      })
      .then((res) => {
        if (res.data?.success) {
          onTreatmentsChange(
            treatments.filter((t) => t.id !== deleteCandidate)
          );
          setDeleteCandidate(null);
          toast.success("Treatment deleted successfully!");
        } else {
          toast.error(res.data?.message || "Failed to delete treatment");
        }
      })
      .catch((error) => {
        console.error(error);
        onTreatmentsChange(previousTreatments);
        toast.error(
          error?.response?.data?.message || "Failed to delete treatment"
        );
      })
      .finally(() => setIsDeletingTreatment(false));
  };

  const handleOpenAddSub = (parentId: string) => {
    setParentTreatmentId(parentId);
    setEditingSub(null);
    setIsSubModalOpen(true);
  };

  const handleOpenEditSub = (parentId: string, sub: ISubTreatment) => {
    setParentTreatmentId(parentId);
    setEditingSub(sub);
    setIsSubModalOpen(true);
  };

  const handleSaveSubTreatment = (subData: ISubTreatment) => {
    if (!parentTreatmentId) return;

    const isEdit = !!editingSub;
    const previousTreatments = treatments;
    const tempClientId = !isEdit ? `st_${Date.now()}` : null;

    const optimisticTreatments = treatments.map((t) => {
      if (t.id !== parentTreatmentId) return t;

      let newSubTreatments: ISubTreatment[];
      if (isEdit && editingSub) {
        newSubTreatments = t.subTreatments.map((s) =>
          s.id === editingSub.id ? { ...s, ...subData, id: editingSub.id } : s
        );
      } else {
        newSubTreatments = [
          ...t.subTreatments,
          { ...subData, id: tempClientId! },
        ];
      }
      return { ...t, subTreatments: newSubTreatments };
    });

    onTreatmentsChange(optimisticTreatments);

    const endpoint = isEdit
      ? "/client/booking/updateBookSubTreatment"
      : "/client/booking/addBookSubTreatment";

    const payload = isEdit
      ? {
          bookTreatmentId: parentTreatmentId,
          subTreatmentId: editingSub!.id,
          subTreatment: subData,
        }
      : {
          bookTreatmentId: parentTreatmentId,
          subTreatment: subData,
        };

    baseUrl
      .patch(endpoint, payload)
      .then((res) => {
        if (res.data?.success) {
          const serverId = res.data?.newSubTreatmentId as string | undefined;
          if (!isEdit && serverId && tempClientId) {
            onTreatmentsChange(
              optimisticTreatments.map((t) => {
                if (t.id !== parentTreatmentId) return t;
                return {
                  ...t,
                  subTreatments: t.subTreatments.map((s) =>
                    s.id === tempClientId
                      ? { ...s, ...subData, id: serverId }
                      : s
                  ),
                };
              })
            );
          }
          closeSubModal();
          toast.success(
            isEdit
              ? "Sub-treatment updated successfully!"
              : "Sub-treatment added successfully!"
          );
        } else {
          onTreatmentsChange(previousTreatments);
          toast.error(res.data?.message || "Failed to save sub-treatment");
        }
      })
      .catch((error) => {
        console.error(error);
        onTreatmentsChange(previousTreatments);
        toast.error(
          error?.response?.data?.message || "Failed to save sub-treatment"
        );
      });
  };

  const handleDeleteSubTreatment = (parentId: string, subId: string) => {
    if (!subId) {
      toast.error("Cannot delete sub-treatment: missing id. Please refresh.");
      return;
    }

    const previousTreatments = treatments;

    onTreatmentsChange(
      treatments.map((t) => {
        if (t.id !== parentId) return t;
        return {
          ...t,
          subTreatments: t.subTreatments.filter((s) => s.id !== subId),
        };
      })
    );

    baseUrl
      .patch("/client/booking/deleteBookSubTreatment", {
        bookTreatmentId: parentId,
        subTreatmentId: subId,
      })
      .then((res) => {
        if (res.data?.success) {
          toast.success("Sub-treatment deleted successfully!");
        } else {
          onTreatmentsChange(previousTreatments);
          toast.error(res.data?.message || "Failed to delete sub-treatment");
        }
      })
      .catch((error) => {
        console.error(error);
        onTreatmentsChange(previousTreatments);
        toast.error(
          error?.response?.data?.message || "Failed to delete sub-treatment"
        );
      });
  };

  return (
    <section className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 pb-3 mb-4 border-b border-slate-200">
        <div>
          <h2 className="text-xs font-bold text-primary mb-0">Treatments</h2>
          <p className="mt-1 text-[11px] text-slate-500">
            All treatment and sub-treatment fields are listed in the tables below.
          </p>
        </div>
        <button
          type="button"
          className="flex shrink-0 items-center gap-1.5 self-start px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
          onClick={handleOpenAddTreatment}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          Add Treatment
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {treatments.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-10 text-center">
            <p className="text-sm font-medium text-slate-600">No treatments yet</p>
            <p className="mt-1 text-xs text-slate-500">
              Click &quot;Add Treatment&quot; to create your first service.
            </p>
          </div>
        ) : (
          treatments.map((t) => (
            <TreatmentCard
              key={t.id}
              treatment={t}
              onDelete={() => setDeleteCandidate(t.id)}
              onEdit={() => handleOpenEditTreatment(t)}
              onAddSub={() => handleOpenAddSub(t.id)}
              onEditSub={(sub) => handleOpenEditSub(t.id, sub)}
              onDeleteSub={(subId) => handleDeleteSubTreatment(t.id, subId)}
            />
          ))
        )}
      </div>

      {deleteCandidate && (
        <ConfirmationModal
          title="Delete Treatment"
          message="Are you sure you want to delete this treatment and all its sub-treatments?"
          onConfirm={handleDeleteTreatment}
          onCancel={() => setDeleteCandidate(null)}
        />
      )}

      {isTreatmentModalOpen && (
        <TreatmentFormModal
          treatment={editingTreatment}
          onClose={closeTreatmentModal}
          onSave={handleSaveTreatment}
        />
      )}

      {isSubModalOpen && (
        <SubTreatmentFormModal
          subTreatment={editingSub}
          onClose={closeSubModal}
          onSave={handleSaveSubTreatment}
        />
      )}
    </section>
  );
};
